import { Options, Rectangle, PolyLine, Polygon, Circle, Ellipse, Icon } from "./Markers.js";
import { S } from "../Squaremap.js";
import L from "leaflet";

class World {
    /** @type {string} */
    name;
    /** @type {string} */
    display_name;
    /** @type {Map<string, L.LayerGroup>} */
    markerLayers;
    /** @type {WorldSettings_PlayerTracker} */
    player_tracker;
    /** @type {number} */
    marker_update_interval;
    /** @type {number} */
    tiles_update_interval;
    /** @type {boolean} */
    staticNeedsMarkerTick;
    /** @type {WorldSettings_Zoom} */
    zoom;
    /** @type {WorldSettings_Spawn} */
    spawn;

    /**
     * @param json {Settings_World}
     */
    constructor(json) {
        this.name = json.name;
        this.order = json.order;
        this.icon = json.icon;
        this.type = json.type;
        this.display_name = json.display_name;
        this.markerLayers = new Map();
        this.player_tracker = {};
        this.marker_update_interval = 5;
        this.tiles_update_interval = 15;
        this.staticNeedsMarkerTick = false;
    }
    tick() {
        // refresh map tile layer
        if (!S.staticMode && S.tick_count % this.tiles_update_interval === 0) {
            S.layerControl.updateTileLayer();
        }
        // load and draw markers
        if (!S.staticMode && S.tick_count % this.marker_update_interval === 0) {
            this.tickMarkers();
        }
        if (S.staticMode && this.staticNeedsMarkerTick) {
            this.tickMarkers();
            this.staticNeedsMarkerTick = false;
        }
    }
    tickMarkers() {
        S.getJSON(
            `tiles/${this.name}/markers.json`,
            (json) => {
                if (this === S.worldList.curWorld) {
                    this.markers(json);
                }
            },
            true,
        );
    }
    unload() {
        S.playerList.clearPlayerMarkers();
        const keys = Array.from(this.markerLayers.keys());
        for (let i = 0; i < keys.length; i++) {
            const layer = this.markerLayers.get(keys[i]);
            S.layerControl.controls.removeLayer(layer);
            layer.remove();
            this.markerLayers.delete(keys[i]);
        }
    }
    /**
     * @param callback {(world: World) => void}
     */
    load(callback) {
        S.getJSON(
            `tiles/${this.name}/settings.json`,
            /** @param {WorldSettings} json */
            (json) => {
                this.player_tracker = json.player_tracker;
                this.zoom = json.zoom;
                this.spawn = json.spawn;
                this.marker_update_interval = json.marker_update_interval;
                this.tiles_update_interval = json.tiles_update_interval;
                this.staticNeedsMarkerTick = true;

                // set the scale for our projection calculations
                S.setScale(this.zoom.max);

                // set center and zoom
                S.centerOn(this.spawn.x, this.spawn.z, this.zoom.def)
                    .setMinZoom(0) // extra zoom out doesn't work :(
                    .setMaxZoom(this.zoom.max + this.zoom.extra);

                // update page (browser tab) title — fixed Agonia branding
                document.title = "Agonia SMP - Karta";

                // setup background
                document.getElementById("map").style.background = this.getBackground();

                // setup tile layers
                S.layerControl.setupTileLayers(this);

                // force clear player markers
                S.playerList.clearPlayerMarkers();

                // tick now, reset counter
                S.tick_count = 0;
                S.tick();

                // force clear player markers
                S.playerList.clearPlayerMarkers();

                if (callback != null) {
                    callback(this);
                }
            },
        );
    }
    getBackground() {
        switch (this.type) {
            case "nether":
                return "url('images/nether_sky.png')";
            case "the_end":
                return "url('images/end_sky.png')";
            case "normal":
            default:
                return "url('images/overworld_sky.png')";
        }
    }
    markers(json) {
        // check if json is iterable
        if (json == null || !(Symbol.iterator in Object(json))) {
            return;
        }
        // iterate layers
        for (const entry of json) {
            // Hide squaremap's built-in spawn-icon layer (we don't use it). The
            // world-border layer is kept and always shown (handled below).
            if (entry.id === "squaremap-spawn_icon") {
                continue;
            }
            // squaremap-worldguard regions: match loosely (the layer id may be namespaced
            // like "worldguard" or "squaremap:worldguard" depending on version). Always
            // show it as "Zoner", pinned to the very bottom of the filter list, visible.
            const isWorldGuard = String(entry.id || "")
                .toLowerCase()
                .includes("worldguard");
            if (isWorldGuard) {
                entry.name = "Zoner";
                entry.order = 1000000;
                entry.control = true;
                entry.hide = false;
            }
            // check if layer exists and needs updating
            let layer = this.markerLayers.get(entry.id);
            if (layer != null) {
                if (layer.timestamp === entry.timestamp) {
                    continue; // skip
                }
                // clear existing layer to rebuild
                S.layerControl.removeOverlay(layer);
                // TODO
                // implement marker tracker instead of clearing
                // to reduce possible client side lag
            }

            // setup the layer
            layer = new L.LayerGroup();
            layer.order = entry.order;
            layer.id = entry.id;
            layer.timestamp = entry.timestamp;
            layer.setZIndex(entry.z_index);
            // Per-category min zoom ("distance"): markers in this layer are only shown/
            // hoverable once zoomed in to at least this level (faded via opacity so they
            // still pan/zoom with the map). Only the fork bakes `min_zoom` into the JSON;
            // on stock squaremap it comes from the add-on's category map, which may load
            // AFTER this layer is built — so we resolve it dynamically in
            // updateZoomVisibility() rather than caching it here.
            layer.bakedMinZoom = entry.min_zoom != null ? entry.min_zoom : null;
            this.markerLayers.set(layer.id, layer);

            // setup the layer control
            if (entry.id === "squaremap-worldborder") {
                // ALWAYS show the world border when one is set — added straight to the
                // map (not a toggleable filter), so the border/corners are always visible.
                layer.addTo(S.map);
            } else if (entry.control === true) {
                S.layerControl.addOverlay(entry.name, layer, entry.hide);
            } else {
                // non-control layers (some addons) still render on the map, just not as
                // a toggle — otherwise their markers/regions would never appear.
                layer.addTo(S.map);
            }

            // setup the markers
            for (const shape in entry.markers) {
                let marker;
                const opts = new Options(entry.markers[shape]);
                // WorldGuard "Zoner": show only the region name in the tooltip — drop the
                // "Owner ..." and "Flags ..." lines (everything after the first <br>).
                if (isWorldGuard) {
                    for (const k of ["popup", "tooltip"]) {
                        if (typeof opts[k] === "string") {
                            opts[k] = opts[k].split(/<br\s*\/?>/i)[0];
                        }
                    }
                }
                switch (opts.pop("type")) {
                    case "rectangle":
                        marker = new Rectangle(opts);
                        break;
                    case "polyline":
                        marker = new PolyLine(opts);
                        break;
                    case "polygon":
                        marker = new Polygon(opts);
                        break;
                    case "circle":
                        marker = new Circle(opts);
                        break;
                    case "ellipse":
                        marker = new Ellipse(opts);
                        break;
                    case "icon":
                        marker = new Icon(opts);
                        break;
                }
                if (marker != null) {
                    marker.addTo(layer);
                }
            }
        }
        this.updateZoomVisibility();
    }
    /**
     * Show/hide zoom-gated layer panes based on the current map zoom level.
     * Called when markers are (re)built, when the category map loads, and on
     * every map zoom change (the "zoomend" handler in Agonia.js — without that
     * one this only ever runs on the 5s marker refresh).
     */
    updateZoomVisibility() {
        const zoom = S.map.getZoom();
        const zmap = window.agoniaCategoryZoom || {};
        for (const layer of this.markerLayers.values()) {
            // Resolve "distance" live: fork-baked value first, else the add-on's map.
            const minZoom =
                layer.bakedMinZoom != null ? layer.bakedMinZoom : zmap[layer.id] != null ? zmap[layer.id] : 0;
            if (!(minZoom > 0)) {
                continue;
            }
            const visible = zoom >= minZoom;
            layer.eachLayer((m) => {
                if (m.setOpacity) {
                    m.setOpacity(visible ? 1 : 0);
                }
                const el = m.getElement && m.getElement();
                if (el) {
                    // when faded out, don't let it catch hovers/clicks
                    el.style.pointerEvents = visible ? "" : "none";
                }
            });
        }
    }
}

export { World };
